import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

/**
 * Error element para rutas de React Router. Muestra una UI amigable
 * cuando una ruta lanza (incluye fallos de lazy import post-deploy).
 *
 * Nota: lazyWithRetry ya intenta un reload automático antes de que este
 * componente se muestre. Si llegamos aquí, el reload falló o el error
 * es genuino.
 */
export default function RouteErrorElement() {
  const error = useRouteError();

  let titulo = 'Algo salió mal';
  let mensaje = 'Ocurrió un error inesperado al cargar esta página.';
  let detalle = '';
  let esActualizacion = false;

  const errorMsg = error instanceof Error ? error.message : String(error ?? '');
  const isChunkError =
    /Failed to fetch dynamically imported module/i.test(errorMsg) ||
    /Loading chunk \d+ failed/i.test(errorMsg) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(errorMsg) ||
    /error loading dynamically imported module/i.test(errorMsg);

  if (isChunkError) {
    esActualizacion = true;
    titulo = 'Nueva actualización lista 👋';
    mensaje = 'Acabamos de mejorar algo por acá. Recargá la página para ver la nueva versión ;)';
  } else if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      mensaje = 'No encontramos la página que buscabas.';
    } else {
      mensaje = `Error ${error.status}`;
      detalle = error.statusText;
    }
  } else if (error instanceof Error) {
    detalle = error.message;
  }

  const handleReload = () => {
    // Intento de "hard reload": limpiar caches y re-registrar SW
    (async () => {
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
      } catch {
        // ignorar
      } finally {
        window.location.reload();
      }
    })();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 ${esActualizacion ? 'bg-primary-100' : 'bg-amber-100'}`}>
          {esActualizacion ? (
            <RefreshCw className="w-8 h-8 text-primary-600" />
          ) : (
            <AlertCircle className="w-8 h-8 text-amber-600" />
          )}
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{titulo}</h1>
        <p className="text-gray-500 mb-6">{mensaje}</p>
        {!esActualizacion && detalle && (
          <p className="text-xs text-gray-400 mb-6 font-mono break-all bg-gray-50 p-2 rounded">
            {detalle}
          </p>
        )}
        <div className="flex gap-3 justify-center">
          {!esActualizacion && (
            <button
              onClick={() => (window.location.href = '/')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Home className="w-4 h-4" />
              Ir al inicio
            </button>
          )}
          <button
            onClick={handleReload}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <RefreshCw className="w-4 h-4" />
            {esActualizacion ? 'Recargar ahora' : 'Recargar'}
          </button>
        </div>
      </div>
    </div>
  );
}
